import React, {
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FaDownload,
  FaQrcode,
  FaShieldAlt,
} from "react-icons/fa";

import { QRCodeCanvas } from "qrcode.react";

import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";

import LoyaltyCardPDF from "./LoyaltyCardPDF";

// ============================================================
// LEVEL
// ============================================================

const getLevelFromPoints = (points) => {
  const p = Number(points || 0);

  if (p >= 1000) return "ELITE";
  if (p >= 600) return "VIP";
  if (p >= 300) return "GOLD";
  if (p >= 150) return "SILVER";
  if (p >= 71) return "BRONZE_PLUS";
  if (p >= 31) return "BRONZE";
  if (p >= 11) return "STARTER";

  return "NEW";
};

// ============================================================
// THEME
// ============================================================

const theme = {
  ELITE: {
    bg: "linear-gradient(135deg,#0f172a,#1e293b)",
    color: "#facc15",
    glow: "#facc15",
  },

  VIP: {
    bg: "linear-gradient(135deg,#facc15,#eab308)",
    color: "#111",
    glow: "#facc15",
  },

  GOLD: {
    bg: "linear-gradient(135deg,#f59e0b,#d97706)",
    color: "#fff",
    glow: "#f59e0b",
  },

  SILVER: {
    bg: "linear-gradient(135deg,#94a3b8,#64748b)",
    color: "#111",
    glow: "#94a3b8",
  },

  BRONZE_PLUS: {
    bg: "linear-gradient(135deg,#6b7280,#374151)",
    color: "#fff",
    glow: "#9ca3af",
  },

  BRONZE: {
    bg: "linear-gradient(135deg,#78350f,#92400e)",
    color: "#fff",
    glow: "#f59e0b",
  },

  STARTER: {
    bg: "linear-gradient(135deg,#e5e7eb,#cbd5e1)",
    color: "#111",
    glow: "#94a3b8",
  },

  NEW: {
    bg: "linear-gradient(135deg,#f3f4f6,#e5e7eb)",
    color: "#111",
    glow: "#9ca3af",
  },
};

// ============================================================
// COMPONENT
// ============================================================

const LoyaltyCard = ({ customer }) => {
  const [downloading, setDownloading] = useState(false);

  // ==========================================================
  // QR CANVAS REF
  // ==========================================================

  const qrCanvasRef = useRef(null);

  // ==========================================================
  // QR TOKEN FROM BACKEND
  // ==========================================================

  const qrToken = useMemo(() => {
    if (!customer) {
      return "";
    }

    return (
      customer.card_qr_token ||
      customer.qr_token ||
      ""
    );
  }, [customer]);

  // ==========================================================
  // QR URL
  //
  // THIS IS THE ACTUAL DATA ENCODED INTO THE QR CODE.
  // ==========================================================

  const qrUrl = useMemo(() => {
    if (!qrToken) {
      return "";
    }

    return `${window.location.origin}/royalty-card/${encodeURIComponent(
      qrToken
    )}`;
  }, [qrToken]);

  // ==========================================================
  // SAFETY
  // ==========================================================

  if (!customer) {
    return null;
  }

  // ==========================================================
  // POINTS
  // ==========================================================

  const points = Number(
    customer.reward_cycle_points ??
      customer.total_points ??
      0
  );

  // ==========================================================
  // LEVEL
  // ==========================================================

  const level =
    customer.level ||
    getLevelFromPoints(points);

  const colors =
    theme[level] || theme.BRONZE;

  // ==========================================================
  // CREATE PDF
  // ==========================================================

  const issueCard = async () => {
    if (!qrToken) {
      alert(
        "This customer does not have a QR token from the backend."
      );

      return;
    }

    try {
      setDownloading(true);

      // ======================================================
      // MAKE SURE QR CANVAS EXISTS
      // ======================================================

      const canvas = qrCanvasRef.current;

      if (!canvas) {
        throw new Error(
          "QR canvas was not found."
        );
      }

      // ======================================================
      // CONVERT REAL QR CANVAS TO PNG DATA URL
      //
      // THIS IS THE IMPORTANT FIX.
      // @react-pdf/renderer needs an IMAGE DATA URL.
      // ======================================================

      const qrDataUrl =
        canvas.toDataURL(
          "image/png"
        );

      if (
        !qrDataUrl ||
        !qrDataUrl.startsWith(
          "data:image/"
        )
      ) {
        throw new Error(
          "Unable to generate QR image."
        );
      }

      console.log(
        "QR TOKEN:",
        qrToken
      );

      console.log(
        "QR URL:",
        qrUrl
      );

      console.log(
        "QR IMAGE GENERATED:",
        qrDataUrl.substring(
          0,
          40
        )
      );

      // ======================================================
      // GENERATE PDF
      // ======================================================

      const blob = await pdf(
        <LoyaltyCardPDF
          customer={customer}
          qrDataUrl={qrDataUrl}
        />
      ).toBlob();

      // ======================================================
      // FILE NAME
      // ======================================================

      const safeName =
        (
          customer.full_name ||
          "customer"
        )
          .replace(
            /[^a-z0-9]/gi,
            "_"
          )
          .replace(
            /_+/g,
            "_"
          );

      // ======================================================
      // DOWNLOAD
      // ======================================================

      saveAs(
        blob,
        `${safeName}-royalty-card.pdf`
      );
    } catch (error) {
      console.error(
        "ROYALTY CARD PDF ERROR:",
        error
      );

      alert(
        `Unable to issue the royalty card.\n\n${
          error?.message ||
          "Please try again."
        }`
      );
    } finally {
      setDownloading(false);
    }
  };

  // ==========================================================
  // STYLES
  // ==========================================================

  const styles = {
    card: {
      position: "relative",
      marginTop: 20,
      padding: 22,
      borderRadius: 18,
      background: colors.bg,
      color: colors.color,
      boxShadow:
        "0 15px 40px rgba(0,0,0,0.25)",
      overflow: "hidden",
    },

    glow: {
      position: "absolute",
      top: "-40px",
      right: "-40px",
      width: 120,
      height: 120,
      background: colors.glow,
      filter: "blur(80px)",
      opacity: 0.4,
      pointerEvents: "none",
    },

    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      position: "relative",
      zIndex: 2,
    },

    brand: {
      fontSize: 16,
      fontWeight: "bold",
      letterSpacing: 2,
    },

    levelBadge: {
      padding: "6px 12px",
      borderRadius: 20,
      background:
        "rgba(255,255,255,0.15)",
      fontSize: 12,
      fontWeight: "bold",
    },

    mainContent: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 20,
      marginTop: 18,
      position: "relative",
      zIndex: 2,
    },

    customerInfo: {
      flex: 1,
      minWidth: 0,
    },

    name: {
      fontSize: 22,
      fontWeight: "700",
    },

    phone: {
      letterSpacing: 3,
      opacity: 0.9,
      marginTop: 4,
    },

    points: {
      marginTop: 18,
      fontSize: 14,
      opacity: 0.9,
    },

    highlight: {
      fontSize: 28,
      fontWeight: "bold",
      marginTop: 6,
    },

    qrSection: {
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },

    qrBox: {
      background: "#ffffff",
      padding: 10,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow:
        "0 8px 20px rgba(0,0,0,0.25)",
    },

    qrLabel: {
      marginTop: 8,
      fontSize: 11,
      fontWeight: "700",
      textAlign: "center",
      color: colors.color,
      display: "flex",
      alignItems: "center",
      gap: 5,
    },

    qrInstruction: {
      marginTop: 4,
      fontSize: 9,
      opacity: 0.8,
      textAlign: "center",
    },

    missingQr: {
      width: 170,
      height: 170,
      background: "#ffffff",
      color: "#991b1b",
      borderRadius: 12,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: 15,
      boxSizing: "border-box",
      fontSize: 12,
      fontWeight: "700",
    },

    footer: {
      marginTop: 18,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      position: "relative",
      zIndex: 2,
    },

    security: {
      display: "flex",
      alignItems: "center",
      gap: 5,
      fontSize: 11,
      opacity: 0.85,
    },

    button: {
      padding: "10px 14px",
      borderRadius: 12,
      border: "none",
      cursor: downloading
        ? "not-allowed"
        : "pointer",
      fontWeight: "600",
      background:
        "rgba(255,255,255,0.18)",
      color: colors.color,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      opacity: downloading ? 0.6 : 1,
    },
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div style={styles.card}>
      <div style={styles.glow} />

      {/* ==================================================
          HEADER
      ================================================== */}

      <div style={styles.header}>
        <div style={styles.brand}>
          ROYALTY SYSTEM
        </div>

        <div style={styles.levelBadge}>
          {level}
        </div>
      </div>

      {/* ==================================================
          MAIN CONTENT
      ================================================== */}

      <div style={styles.mainContent}>

        {/* CUSTOMER INFORMATION */}

        <div style={styles.customerInfo}>
          <div style={styles.name}>
            {customer.full_name}
          </div>

          <div style={styles.phone}>
            {customer.phone}
          </div>

          <div style={styles.points}>
            Balance Points
          </div>

          <div style={styles.highlight}>
            {points}
          </div>
        </div>

        {/* ==================================================
            QR CODE
        ================================================== */}

        <div style={styles.qrSection}>
          {qrToken ? (
            <>
              <div style={styles.qrBox}>
                <QRCodeCanvas
                  ref={qrCanvasRef}
                  value={qrUrl}
                  size={170}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="H"
                  includeMargin={true}
                  style={{
                    display: "block",
                  }}
                />
              </div>

              <div style={styles.qrLabel}>
                <FaQrcode />
                SCAN ROYALTY CARD
              </div>

              <div style={styles.qrInstruction}>
                Scan to identify this customer
              </div>
            </>
          ) : (
            <div style={styles.missingQr}>
              <FaQrcode
                size={35}
                style={{
                  marginBottom: 8,
                }}
              />

              QR CODE UNAVAILABLE

              <div
                style={{
                  fontSize: 9,
                  marginTop: 6,
                  fontWeight: "400",
                }}
              >
                The backend did not provide
                a card QR token.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ==================================================
          FOOTER
      ================================================== */}

      <div style={styles.footer}>
        <div style={styles.security}>
          <FaShieldAlt />
          Secure Royalty Card
        </div>

        <button
          type="button"
          style={styles.button}
          onClick={issueCard}
          disabled={
            downloading || !qrToken
          }
        >
          <FaDownload />

          {downloading
            ? "Issuing..."
            : "Issue Royalty Card"}
        </button>
      </div>
    </div>
  );
};

export default LoyaltyCard;